import React, { useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import { DateTime } from "luxon";
import jssVariables from "../../jss_setup";
import tinycolor from "tinycolor2";
import { useContext } from "react";
import {
  CurrencyContext,
  TranslationContext,
  currencies,
} from "../../umbilical_tools";
import { getExchangedPrice } from "../../umbilical_hocs";
import { Button } from "../../components/common/Button";
import { useSelector } from "react-redux";
import { t } from "i18next";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { getCarrierIcon } from "../../api/trains";

const carrierIconStyles = createUseStyles({
  CarrierIcon: (props) => ({
    ..._.get(props, "customStyles.CarrierIcon", {}),
  }),
  icon: (props) => ({
    width: "100%",
    height: "100%",
    maxHeight: `calc(${jssVariables.navbar_height} / 2)`,
    maxWidth: `calc(${jssVariables.navbar_height} * 2)`,
    ..._.get(props, "customStyles.icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {},
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export const CarrierIcon = ({ carrier }) => {
  const [icon, setIcon] = useState(null);
  const classes = carrierIconStyles({});

  async function onGetCarrierIcon(carrier) {
    const operatorIcon = await getCarrierIcon(_.lowerCase(carrier));
    setIcon(operatorIcon);
  }

  useEffect(() => {
    onGetCarrierIcon(carrier);
  }, [carrier]);

  return (
    <div className={classes.CarrierIcon}>
      {icon ? (
        <img src={icon} className={classes.icon} />
      ) : (
        _.capitalize(carrier)
      )}
    </div>
  );
};
CarrierIcon.defaultProps = {
  carrier: "",
};
CarrierIcon.propTypes = {
  carrier: PropTypes.string.isRequired,
};

const styles = createUseStyles({
  FairConditions: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    paddingTop: jssVariables.normal_gap,
    ..._.get(props, "customStyles.FairConditions", {}),
  }),
  header: (props) => ({
    color: jssVariables.colors.base_color,
    fontSize: "x-large",
    ..._.get(props, "customStyles.header", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {},
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export const FairConditions = ({ selectedType, returnRoute }) => {
  const { t } = useContext(TranslationContext);
  const classes = styles({});

  return (
    <div className={classes.FairConditions}>
      <div className={classes.header}>{t("fairConditions")}</div>
      <div
        className={classes.body}
        dangerouslySetInnerHTML={{
          __html: returnRoute
            ? selectedType.returnRoute.conditions
            : selectedType.goRoute.conditions,
        }}
      />
    </div>
  );
};
FairConditions.defaultProps = {
  selectedType: {},
  returnRoute: false,
};
FairConditions.propTypes = {
  selectedType: PropTypes.object.isRequired,
  returnRoute: PropTypes.bool.isRequired,
};

const itineraryStyles = createUseStyles({
  Itinerary: (props) => ({
    display: "grid",
    position: "relative",
    top: `calc(${jssVariables.double_gap} * -1)`,
    borderRadius: jssVariables.half_gap,
    border: `1px solid ${jssVariables.colors.border}`,
    padding: jssVariables.normal_gap,
    paddingTop: jssVariables.double_gap,
    margin: [0, jssVariables.half_gap],
    zIndex: -1,
    ..._.get(props, "customStyles.Itinerary", {}),
  }),
  body: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr",
    fontSize: "medium",
    ..._.get(props, "customStyles.body", {}),
  }),
  stationInfoDate: (props) => ({
    color: "rgba(0, 0, 0, 0.4)",
    paddingLeft: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.stationInfoDate", {}),
  }),
  services: (props) => ({
    color: "rgba(0, 0, 0, 0.4)",
    paddingLeft: `calc(${jssVariables.normal_gap} * 2)`,
    height: `calc(${jssVariables.double_gap} * 2)`,
    ..._.get(props, "customStyles.services", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    paddingTop: jssVariables.half_gap,
    color: jssVariables.colors.base_color,
    fontSize: "x-large",
    ..._.get(props, "customStyles.header", {}),
  }),
  stationInfo: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    alignItems: "center",
    gridGap: jssVariables.normal_gap,
    position: "relative",
    width: "95%",
    fontWeight: "500",
    paddingLeft: jssVariables.double_gap,
    "&:before": {
      content: `''`,
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      height: `calc(${jssVariables.normal_gap} * 1.3)`,
      width: `calc(${jssVariables.normal_gap} * 1.3)`,
      borderRadius: "50%",
      border: `3px solid ${jssVariables.colors.base_color}`,
    },
    "&:not(:last-child):after": {
      content: `''`,
      position: "absolute",
      left: ".37rem",
      top: `calc(${jssVariables.normal_gap} * 2)`,
      height: `calc(${jssVariables.normal_gap} * 5.55)`,
      borderLeft: `3px dashed ${jssVariables.colors.base_color}`,
    },
    ..._.get(props, "customStyles.stationInfo", {}),
  }),
  divider: (props) => ({
    width: "100%",
    height: "1px",
    background: jssVariables.colors.border,
    margin: `calc(${jssVariables.normal_gap} * 1) 0`,
    ..._.get(props, "customStyles.divider", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {},
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export const Itinerary = ({ expanded, data, selectedType, returnRoute }) => {
  const classes = itineraryStyles({});
  const segments = _.get(data, "segments", []);

  return (
    <div className={classes.Itinerary}>
      {expanded && (
        <React.Fragment>
          <FairConditions
            selectedType={selectedType}
            returnRoute={returnRoute}
          />
          <div className={classes.divider} />
        </React.Fragment>
      )}
      {segments.map((segment, idx) => (
        <React.Fragment key={idx}>
          <div className={classes.header}>
            {segment.vehicle.type} #{segment.vehicle.reference}
            {/* <InlineIcon
              icon="fluent-emoji-high-contrast:name-badge"
              className={classes.arrowIcon}
            /> */}
          </div>
          <div className={classes.body}>
            <div className={classes.stationInfo}>
              {DateTime.fromISO(segment.departure).toLocal().toFormat("hh:mm")}{" "}
              {segment.origin.label}
            </div>
            <div className={classes.stationInfoDate}>
              {DateTime.fromISO(segment.departure).toLocaleString(
                DateTime.DATE_MED_WITH_WEEKDAY
              )}
            </div>
            <div className={classes.services}>
              {segment.services
                ? segment.services.map((service) => service.label).join(", ")
                : t("noServices")}
            </div>
            <div className={classes.stationInfo}>
              {DateTime.fromISO(segment.arrival).toLocal().toFormat("hh:mm")}{" "}
              {segment.destination.label}
            </div>
          </div>
          {idx + 1 !== segments.length && <div className={classes.divider} />}
        </React.Fragment>
      ))}
    </div>
  );
};
Itinerary.defaultProps = {
  expanded: false,
  data: {},
  returnRoute: false,
  selectedType: {},
};
Itinerary.propTypes = {
  data: PropTypes.object.isRequired,
  expanded: PropTypes.bool.isRequired,
  returnRoute: PropTypes.bool.isRequired,
  selectedType: PropTypes.object.isRequired,
};

const selectedOptionStyles = createUseStyles({
  SelectedOption: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    position: "relative",
    borderRadius: jssVariables.half_gap,
    padding: jssVariables.normal_gap,
    boxSizing: "border-box",
    ..._.get(props, "customStyles.SelectedOptions", {}),
  }),
  body: (props) => ({
    fontSize: "medium",
    ..._.get(props, "customStyles.body", {}),
  }),
  stationInfoDate: (props) => ({
    color: "rgba(0, 0, 0, 0.4)",
    paddingLeft: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.stationInfoDate", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    color: jssVariables.colors.base_color,
    fontSize: "x-large",
    ..._.get(props, "customStyles.header", {}),
  }),
  stationInfo: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    alignItems: "center",
    gridGap: jssVariables.normal_gap,
    position: "relative",
    width: "95%",
    fontWeight: "500",
    paddingLeft: jssVariables.double_gap,
    "&:before": {
      content: `''`,
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      height: `calc(${jssVariables.normal_gap} * 1.3)`,
      width: `calc(${jssVariables.normal_gap} * 1.3)`,
      borderRadius: "50%",
      border: `3px solid ${jssVariables.colors.base_color}`,
    },
    "&:not(:last-child):after": {
      content: `''`,
      position: "absolute",
      left: ".37rem",
      top: `calc(${jssVariables.normal_gap} * 2)`,
      height: `calc(${jssVariables.normal_gap} * 3.55)`,
      width: "0rem",
      borderLeft: `3px dashed ${jssVariables.colors.base_color}`,
    },
    ..._.get(props, "customStyles.stationInfo", {}),
  }),
  footer: (props) => ({
    display: "grid",
    gridGap: jssVariables.double_gap,
    // gridTemplateColumns: "max-content 1fr",
    // placeContent: "space-between",
    color: "#00000066",
    fontSize: "large",
    ..._.get(props, "customStyles.footer", {}),
  }),
  price: (props) => ({
    justifySelf: "end",
    ..._.get(props, "customStyles.price", {}),
  }),
  divider: (props) => ({
    width: "100%",
    height: "1px",
    background: jssVariables.colors.border,
    margin: `calc(${jssVariables.normal_gap} * 1) 0`,
    ..._.get(props, "customStyles.divider", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    SelectedOption: (props) => ({
      padding: [jssVariables.normal_gap, jssVariables.double_gap],
      ..._.get(props, "customStyles.SelectedOptions", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export const SelectedOption = ({
  selectedTrain,
  selectedType,
  returnRoute,
}) => {
  const classes = selectedOptionStyles({});

  const { currency: settedCurrency } = useContext(CurrencyContext);

  const segments = _.get(selectedTrain, "segments", []);
  const ticketTitle = _.get(
    selectedType,
    returnRoute ? "returnRoute.title" : "goRoute.title",
    ""
  );
  const price = _.get(
    selectedType,
    returnRoute ? "returnRoute.price.value" : "goRoute.price.value",
    ""
  );
  const priceCurrency = _.get(
    selectedType,
    returnRoute ? "returnRoute.price.currency" : "goRoute.price.currency",
    ""
  );

  return (
    <div className={classes.SelectedOption}>
      {segments.map((segment, idx) => (
        <React.Fragment key={idx}>
          <div className={classes.header}>
            {segment.vehicle.type} #{segment.vehicle.reference}
            <CarrierIcon carrier={segment.marketing_carrier} />
          </div>
          <div className={classes.body}>
            <div className={classes.stationInfo}>
              {DateTime.fromISO(segment.departure).toLocal().toFormat("hh:mm")}{" "}
              {segment.origin.label}
            </div>
            <div className={classes.stationInfoDate}>
              {DateTime.fromISO(segment.departure).toLocaleString(
                DateTime.DATE_MED_WITH_WEEKDAY
              )}
            </div>
            <br />
            <div className={classes.stationInfo}>
              {DateTime.fromISO(segment.arrival).toLocal().toFormat("hh:mm")}{" "}
              {segment.destination.label}
            </div>
          </div>
          {idx === segments.length - 1 && (
            <div className={classes.footer}>
              <div className={classes.ticketTitle}>{ticketTitle}</div>
              <div className={classes.price}>
                {currencies[settedCurrency]}
                {getExchangedPrice({
                  price: price,
                  priceCurrency: priceCurrency,
                  settedCurrency,
                  withDecimals: true,
                })}
              </div>
            </div>
          )}
          {idx + 1 !== segments.length && <div className={classes.divider} />}
        </React.Fragment>
      ))}
    </div>
  );
};
SelectedOption.defaultProps = {
  selectedTrain: {},
  selectedType: {},
  returnRoute: false,
};
SelectedOption.propTypes = {
  selectedTrain: PropTypes.object.isRequired,
  selectedType: PropTypes.object.isRequired,
  returnRoute: PropTypes.bool.isRequired,
};

const selectedOptionsStyles = createUseStyles({
  SelectedOptions: (props) => ({
    paddingBottom: jssVariables.normal_gap,
    ..._.get(props, "customStyles.SelectedOptions", {}),
  }),
  header: (props) => ({
    background: tinycolor(jssVariables.colors.base_color)
      .lighten(35)
      .toRgbString(),
    color: jssVariables.colors.base_color,
    padding: [jssVariables.half_gap, jssVariables.normal_gap],
    borderRadius: [jssVariables.half_gap, jssVariables.half_gap, 0, 0],
    fontSize: "large",
    fontWeight: "bold",
    textTransform: "capitalize",
    ..._.get(props, "customStyles.header", {}),
  }),
  divider: (props) => ({
    background: jssVariables.colors.secondary_color,
    height: "3px",
    width: `calc(100% - ${jssVariables.normal_gap})`,
    margin: jssVariables.half_gap,
    ..._.get(props, "customStyles.divider", {}),
  }),
  selectionsContr: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    borderRadius: jssVariables.half_gap,
    background: tinycolor(jssVariables.colors.base_color)
      .lighten(47)
      .toRgbString(),
    paddingBottom: jssVariables.normal_gap,
    ..._.get(props, "customStyles.selectionsContr", {}),
  }),
  info: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content max-content",
    alignItems: "center",
    gridGap: jssVariables.normal_gap,
    color: jssVariables.colors.base_color,
    fontSize: "large",
    fontWeight: "500",
    paddingTop: jssVariables.normal_gap,
    paddingBottom: jssVariables.normal_gap,
    ..._.get(props, "customStyles.info", {}),
  }),
  priceLabel: (props) => ({
    color: "rgba(0, 0, 0, 0.4)",
    ..._.get(props, "customStyles.priceLabel", {}),
  }),
  price: (props) => ({
    fontSize: "x-large",
    lineHeight: "1",
    ..._.get(props, "customStyles.price", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    SelectedOptions: (props) => ({
      alignContent: "baseline",
      ..._.get(props, "customStyles.SelectedOptions.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {},
});
export const SelectedOptions = ({
  selectedTrain,
  selectedType,
  isRoundTrip,
  page_session_id,
}) => {
  const classes = selectedOptionsStyles({});
  const { t } = useContext(TranslationContext);
  const { currency: settedCurrency } = useContext(CurrencyContext);

  const { adults, seniors, youths } = useSelector(
    (state) => state.TrainSearchForm
  );

  const goRoutePrice = _.get(selectedType, "goRoute.price.value", 0);
  const returnRoutePrice = _.get(selectedType, "returnRoute.price.value", 0);
  const roundTripPrice = goRoutePrice + returnRoutePrice;
  const priceCurrency = _.get(selectedType, "goRoute.price.currency", "");
  const totalPrice = roundTripPrice;

  const history = useHistory();

  function onHandleBook(page_session_id) {
    if (
      (isRoundTrip && !selectedType["returnRoute"]) ||
      !selectedType["goRoute"]
    ) {
      toast.warning(t("ticketError"), {
        position: "top-center",
        autoClose: 3000,
      });
    } else {
      const data = {
        selectedType,
        selectedTrain,
        totalPrice: totalPrice,
      };

      const encodedData = encodeURIComponent(JSON.stringify(data));
      history.push({
        pathname: `/trains/booking_process/${page_session_id}`,
        state: { encodedData },
      });
    }
  }
  return (
    <div className={classes.SelectedOptions}>
      <div className={classes.selectionsContr}>
        <div className={classes.header}>{t("selected")}</div>
        {selectedTrain["goRoute"] && (
          <SelectedOption
            returnRoute={false}
            selectedTrain={selectedTrain["goRoute"]}
            selectedType={selectedType}
          />
        )}

        {selectedTrain["returnRoute"] && (
          <React.Fragment>
            {selectedTrain["goRoute"] && <div className={classes.divider} />}
            <SelectedOption
              returnRoute={true}
              selectedTrain={selectedTrain["returnRoute"]}
              selectedType={selectedType}
            />
          </React.Fragment>
        )}
      </div>
      {selectedType["goRoute"] && (
        <div className={classes.info}>
          {t("price")} {t("for")}{" "}
          {adults && adults > 1
            ? `${adults} ${t("adults")}`
            : adults !== 0 && `${adults} ${t("adult")}`}
          {(seniors > 0 || youths > 0) && adults !== 0 && ","}{" "}
          {seniors > 1
            ? `${seniors} ${t("seniors")}`
            : seniors !== 0 && `${seniors} ${t("senior")}`}
          {youths > 0 && seniors !== 0 && ","}{" "}
          {youths > 1
            ? `${youths} ${t("youths")}`
            : youths !== 0 && `${youths} ${t("youth")}`}{" "}
          <span className={classes.priceLabel}>{t("total")}</span>
          <span className={classes.price}>
            {currencies[settedCurrency]}
            {getExchangedPrice({
              price: totalPrice,
              priceCurrency: priceCurrency,
              settedCurrency,
              withDecimals: true,
            })}
          </span>
        </div>
      )}
      <Button
        id="trainBookBtn"
        text={t("book")}
        onClick={() => onHandleBook(page_session_id)}
        size="large"
        customStyles={{
          ButtonDefault: {
            padding: [jssVariables.half_gap, jssVariables.double_gap],
          },
        }}
      />
    </div>
  );
};
SelectedOptions.defaultProps = {
  selectedTrain: {},
  selectedType: {},
  isRoundTrip: false,
  page_session_id: "",
};
SelectedOptions.propTypes = {
  selectedTrain: PropTypes.object.isRequired,
  selectedType: PropTypes.object.isRequired,
  isRoundTrip: PropTypes.bool.isRequired,
  page_session_id: PropTypes.string.isRequired,
};
